import { Button } from '@crowdfpl/ui-web/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/helpers/utils';
import { useIsMobile, useIsMobileNav } from '@/hooks/utils/use-mobile';

export interface RightAction {
  type: 'none' | 'text' | 'icon';
  label?: string;
  icon?: React.ReactNode;
}

export interface AppBarProps {
  showBack?: boolean;
  title?: string;
  breadcrumbs?: string[];
  rightAction?: RightAction;
  onBack?: () => void;
  onRightAction?: () => void;
  elevation?: number;
  className?: string;
  hasMobileHeader?: boolean; // Whether there's a MobileHeader above this AppBar on mobile
}

export function AppBar({
  showBack = false,
  title = '',
  breadcrumbs,
  rightAction = { type: 'none' },
  onBack,
  onRightAction,
  elevation = 1,
  className,
  hasMobileHeader = false,
}: AppBarProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isMobileNav = useIsMobileNav();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (!breadcrumbs) return;
    const breadcrumb = breadcrumbs[index];
    if (breadcrumb === 'Duels') navigate('/duels');
    else if (breadcrumb === 'Leaderboard') navigate('/leaderboard');
    else if (breadcrumb === 'Home') navigate('/');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && (showBack || breadcrumbs)) {
      handleBack();
    }
  };

  return (
    <header
      className={cn(
        'sticky flex items-center h-12 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border',
        !isMobileNav ? 'top-16 z-40' : hasMobileHeader ? 'top-14 z-30' : 'top-0 z-30',
        elevation === 0 && 'shadow-none',
        elevation === 1 && 'shadow-sm',
        elevation === 2 && 'shadow-md',
        className,
      )}
      role="banner"
      onKeyDown={handleKeyDown}
      tabIndex={showBack ? 0 : -1}
    >
      <div className={cn('flex items-center justify-between w-full safe-area-inset-top', isMobile ? 'px-3' : 'container mx-auto px-4')}>
        {!isMobile && breadcrumbs ? (
          <nav className="flex items-center flex-1" role="navigation" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-1 text-sm text-muted-foreground">
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && <ChevronRight className="h-4 w-4 mx-1" />}
                  {index < (breadcrumbs?.length || 0) - 1 ? (
                    <button
                      onClick={() => handleBreadcrumbClick(index)}
                      className="hover:text-foreground transition-colors"
                      aria-label={`Navigate to ${crumb}`}
                    >
                      {crumb}
                    </button>
                  ) : (
                    <span className="font-medium text-foreground" aria-current="page">
                      {crumb}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        ) : (
          <>
            {isMobile && showBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="flex items-center justify-center w-11 h-11 p-0 hover:bg-accent"
                aria-label="Back"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}
            <h1
              className={cn(
                'text-base font-semibold truncate',
                rightAction?.type === 'none' ? 'text-center flex-1' : 'flex-1 text-center -ml-11',
                !(isMobile && showBack) && 'ml-0',
              )}
            >
              {title}
            </h1>
          </>
        )}

        <div className="flex items-center justify-end w-11 h-11">
          {rightAction?.type === 'text' && rightAction.label && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRightAction}
              className="text-primary hover:text-primary/80 h-11 px-3"
              aria-label={rightAction.label}
            >
              {rightAction.label}
            </Button>
          )}
          {rightAction?.type === 'icon' && rightAction.icon && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRightAction}
              className="flex items-center justify-center w-11 h-11 p-0 hover:bg-accent"
              aria-label="Action"
            >
              {rightAction.icon}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

